import React from "react";
import { NodeProps, useReactFlow } from "reactflow";
import axios from "axios";
import { TextInput } from "../../../../form/textInput";
import { Checkboxs } from "../../../../form/checkboxs";
import { operationType, selectDataType } from "../../../../../services/types";
import { updateNodeValue } from "../../../../../services/method";
import { Select } from "../../../../form/select";
import { Main } from "./style";
import { Textarea } from "../../../../form/textarea";
import { getGoogleAccountToken } from "../../../../auth/method";
import { AuthenticateSelect } from "../../../../auth/select";
import { SettingsBlock, settingsDataType } from "../../../../settingsBlock";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";
import { ExpandedBlock } from "../../../expanded";
import { Styled } from "./style";
import { RootState } from "../../../../../redux/store";
import { useSelector } from "react-redux";

type SpreadsheetProps = NodeProps<operationType> & {
   setChildMinimize?: (fn: (id: string) => void) => void;
   setChildSettings?: (fn: (id: string) => void) => void;
};

export function Spreadsheet({ id, data, setChildMinimize, setChildSettings }: SpreadsheetProps) {
   const isWorkflowRun = useSelector((state: RootState) => state.settings.data.isWorkflowRun);
   const [requests, setRequests] = React.useState<selectDataType[]>([
      { id: 1, name: 'Create Spreadsheet', refId: 'create_spreadsheet', isActive: true }, 
      { id: 2, name: 'Delete Spreadsheet', refId: 'delete_spreadsheet' },
      { id: 3, name: 'Append or update row', refId: 'upsert_row_value' },
      { id: 4, name: 'Append Row', refId: 'append_row' },
      { id: 5, name: 'Append Multi Row', refId: 'append_multi_row' },
      { id: 6, name: 'Update Row', refId: 'update_row' },
      { id: 7, name: 'Update Multi Row', refId: 'update_multi_row' },
      { id: 8, name: 'Clear Sheet', refId: 'clear_sheet' },
      { id: 9, name: 'Create Sheet', refId: 'create_sheet' },
      { id: 10, name: 'Delete Sheet', refId: 'delete_sheet' },
      { id: 11, name: 'Delete columns from sheet', refId: 'delete_columns' },
      { id: 12, name: 'Delete rows from sheet', refId: 'delete_rows' },
      { id: 13, name: 'Get column(s) in sheet', refId: 'get_column' },
      { id: 14, name: 'Get row(s) in sheet', refId: 'get_row' },
      { id: 15, name: 'Let AI Agent Decide', refId: 'agent_decide' }
   ]);
   const { setNodes } = useReactFlow();
   const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
   const [isExpanded, setIsExpanded] = React.useState(false);
   const [isCanFetch, setIsCanFetch] = React.useState(false);
   
   // --
   React.useEffect(() => {
      if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev) });
      if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })) });
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [setChildMinimize, setChildSettings]);

   // ---
   React.useEffect(() => {
      if (!data.value) {
         updateNodeValue(id, setNodes, { request: requests[0].refId, sheet_name: 'Sheet1', any: '' });
      } else {
         setRequests(requests => requests.map(request => ({ ...request, isActive: request.refId === data.value.request })));
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   // --
   React.useEffect(() => {
      if (!isCanFetch && isWorkflowRun && data.value?.request === 'delete_spreadsheet') {
         setIsCanFetch(true);
      } else if (isCanFetch && !isWorkflowRun) {
         handleActions('fetch');
         setIsCanFetch(false); 
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [isCanFetch, isWorkflowRun, data.value?.request]);

   // ---
   const handleAuthenticate = async (type: 'add' | 'remove', data: any) => {
      if (type === 'add') { 
         updateNodeValue(id, setNodes, { connection: data });
      } else if (type === 'remove') {
         updateNodeValue(id, setNodes, { connection: null });
      }
   }

   // ---
   const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>, keyRef?: string) => {
      const value = e.target.value;
      if (keyRef) updateNodeValue(id, setNodes, { [keyRef]: value });
   }

   // ---
   const handleActions = async (type: 'fetch' | 'remove' | 'request' | 'header', info?: any) => {
      if (type === 'fetch') {
         try {
            updateNodeValue(id, setNodes, { isLoading: true });
            const token = await getGoogleAccountToken(data.value?.connection, id, setNodes);
            const response = (await axios.get(`/project_management/spreadsheet/user_spreadsheets?token=${token}`)).data;
            updateNodeValue(id, setNodes, { files: response.spreadsheets, isLoading: false });
         } catch {
            updateNodeValue(id, setNodes, { isLoading: false });
         }
      } else if (type === 'remove') {
         updateNodeValue(id, setNodes, { files: null, spreadsheet_id: '' });

      } else if (type === 'request') {
         const refId = requests.find(obj => obj.id === info)?.refId;
         setRequests((requests) => requests.map((request) => {
            if (request.id === info) return { ...request, isActive: true };
            else return { ...request, isActive: false };
         }));

         setNodes(nodes => nodes.map(node => {
            if (node.id === id) {
               return { ...node, data: { ...node.data, value: { 
                  ...node.data.value, request: refId, any: '', end_range: '', area: '', search_value: '' 
               }}}
            } else return node;
         }));
      }
   }

   // ---
   const isFilesEdit = data.value?.request !== 'create_spreadsheet' && data.value?.files?.length;
   const isWarning = !data.value?.connection;

   return (<>
      <SettingsBlock nodeId={ id }data={ settings } setData={ setSettings } />
      <Main>
         { isWarning ? <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={21} /></span>
         </> : data.output ? <>
            <div id="ran"></div>
            <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
         </> : null }
         { isExpanded ? 
         <ExpandedBlock
            data={{ id, ...data }} 
            handleClose={ () => setIsExpanded(false) } 
            isWarning={ isWarning }
            styleParent={ (isFilesEdit ? { right: '-101.5px' } : {}) }
            style={{ minWidth: '420px' }}
         ><Styled>
            <AuthenticateSelect type="google" defaultId={ data.value?.connection?.id } handleAuthenticate={ handleAuthenticate } background={ data.background } />
            <div className="action">
               { data.value?.connection && data.value?.request !== 'create_spreadsheet' ? <>
                  <button className="btn1 withBorder" onClick={ _=> handleActions('fetch') } disabled={ data.value?.isLoading }>Fetch Documents</button> 
                  { isFilesEdit ? <button className="btn1 withBorder delete" onClick={ _=> handleActions('remove') } disabled={ data.value?.isLoading }>Revert Documents</button> : null }
               </>: null }
            </div>
            <Select title="Request" data={ requests } handleSelectItem={ (id) => handleActions('request', id) } isSmall={ true } />
            { !['create_spreadsheet', 'delete_spreadsheet'].includes(data.value?.request) ? <TextInput keyRef="sheet_name" title="Sheet" placeholder="sheet_name" value={ data.value?.sheet_name || '' } handleChange={ handleChangeInput } /> : null }
            { !['delete_spreadsheet', 'clear_sheet', 'create_sheet', 'delete_sheet'].includes(data.value?.request) ? <TextInput 
               keyRef="any" // @ts-ignore
               title={ inputAny[data.value?.request]?.title } // @ts-ignore
               placeholder={ inputAny[data.value?.request]?.placeholder }
               value={ data.value?.any || '' } 
               handleChange={ handleChangeInput } 
            /> : null }
            { ['upsert_row_value', 'update_row', 'update_multi_row'].includes(data.value?.request) ? <TextInput keyRef="search_value" title="Search" placeholder="search_value (Required)" value={ data.value?.search_value } handleChange={ handleChangeInput } /> : null }
            { ['upsert_row_value', 'update_multi_row', 'delete_columns', 'delete_rows', 'get_column', 'get_row'].includes(data.value?.request) ? <TextInput 
                  keyRef="end_range"
                  title={ ['upsert_row_value', 'update_multi_row'].includes(data.value?.request) ? 'Start' : 'End' } // @ts-ignore
                  placeholder={ inputAny[data.value?.request]?.endRange } 
                  value={ data.value?.end_range } 
                  handleChange={ handleChangeInput }
               /> : null }
            { ['append_row', 'append_multi_row', 'upsert_row_value', 'update_row', 'update_multi_row'].includes(data.value?.request) ?
               <Textarea 
                  keyRef="area" 
                  value={ data.value?.area } // @ts-ignore
                  placeholder={ inputAny[data.value?.request]?.area } // @ts-ignore
                  handleChange={ (e: any, keyRef?: string) => updateNodeValue(id, setNodes, { [keyRef]: e.target.value }) } 
               /> 
            : null }
            { isFilesEdit ? <div className="filesEdit">
               <h3 className="title" style={{ backgroundColor: data.background, color: '#fff' }}>Documents ({ data.value.files.length })</h3>
               <div className="scroll nowheel">
                  <Checkboxs 
                     data={ data.value.files.map((file: selectDataType) => ({ ...file, isActive: file.id === data.value.spreadsheet_id })) } 
                     handelCheckbox={ (sheetId: any) =>{ updateNodeValue(id, setNodes, { spreadsheet_id: data.value.spreadsheet_id === sheetId ? '' : sheetId })} } 
                  />
               </div>
            </div> : null }
         </Styled></ExpandedBlock> : null}
      </Main>
   </>);
}

// ---
const inputAny = { 
   create_spreadsheet: { title: 'Title', placeholder: 'title_spreadsheet (Required)' },
   get_row: { title: 'Start', placeholder: 'row_number', endRange: 'row_number' },
   get_column: { title: 'Start', placeholder: 'column_letter', endRange: 'column_letter' },
   upsert_row_value: { 
      title: 'Column', placeholder: 'column_search_letter (Default A)', 
      area: 'Your input should look like: \n["column1 value", "column2 value"]',
      endRange: 'column_start_add (Optional)'
   },
   append_row: { title: 'Column', placeholder: 'column_start_letter (Default A)', area: 'Your input should look like: \n["column1 value", "column2 value"]' },
   append_multi_row: { title: 'Column', placeholder: 'column_start_letter (Default A)', area: '[\n["column1 value", "column2 value"],\n["column1 value", "column2 value"]\n]' },
   update_row: { title: 'Column', placeholder: 'column_search_letter (Default A)', area: 'Your input should look like: \n["column1 value", "column2 value"]' },
   update_multi_row: { 
      title: 'Column', placeholder: 'column_search_letter (Default A)', 
      area: '[\n["column1 value", "column2 value"],\n["column1 value", "column2 value"]\n]',
      endRange: 'column_start_add (Optional)'
   },
   delete_columns: { title: 'Start', placeholder: 'column_letter', endRange: 'column_letter' },
   delete_rows: { title: 'Start', placeholder: 'row_number', endRange: 'row_number' }
};